import { ICardApplicationForm } from '~/interfaces/cardApplication';
import { CardDataPointsShared } from '~/schemas/DTO/cardApplicationSchemas';
import { AppDispatch } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';

const getCardApplicationFormFieldValueByDataPointShared = (
  dataPointName: keyof CardDataPointsShared,
  form: ICardApplicationForm
) => {
  switch (dataPointName) {
    case 'company':
      return (
        form.organizationNumber ||
        form.creditReportProvider?.creditReportProviderNumber
      );
    case 'turnover':
      return form.turnover;
    case 'cardPurpose':
      return form.purposeOfCard;
    case 'cardPurposeReason':
      return form.cardPurposeManual;
    case 'applicantEmail':
      return form.applicant.email;
    case 'applicantPhone':
      return form.applicant.phone;
    case 'applicantPersonalNumber':
      return form.applicant.personalNumber;
    case 'applicantDateOfBirth':
      return form.applicant.dateOfBirth;
    case 'applicantFullName':
      return form.applicant.firstName && form.applicant.lastName;
    case 'applicantAddress':
      return form.applicant.address;
    default: {
      // Exhaustiveness check: TypeScript will error if a case is missing
      const _exhaustiveCheck: never = dataPointName;
      return _exhaustiveCheck;
    }
  }
};

/**
 * Determine which data points need to be shared based on the form values and already shared data points.
 * It should only be marked as to be shared if it has a value in the form and has not yet been shared.
 */
export const determineNewDataPointsToShareForCardApplication = (
  form: ICardApplicationForm,
  dataPointsShared: CardDataPointsShared
): Partial<CardDataPointsShared> => {
  const newDataPointsToShare: Partial<CardDataPointsShared> = {};
  const typedDataPointsShared = Object.keys(dataPointsShared) as Array<
    keyof CardDataPointsShared
  >;

  for (const key of typedDataPointsShared) {
    if (!dataPointsShared[key]) {
      const formValue = getCardApplicationFormFieldValueByDataPointShared(
        key,
        form
      );

      if (formValue) {
        newDataPointsToShare[key] = true;
      }
    }
  }

  return newDataPointsToShare;
};

/**
 * Dispatch tracking events for shared data points.
 */
export const dispatchCardApplicationDataPointTrackingEvents = (
  dispatch: AppDispatch,
  dataPointsToShare: Partial<CardDataPointsShared>
) => {
  for (const dataPointName of Object.keys(dataPointsToShare)) {
    const actionName = `${dataPointName}_data_point_shared`;

    dispatch(
      trackCardApplicationActions({
        actionName,
      })
    );
  }
};
